import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';

export interface Note {
  id: string;
  content: {
    title: string;
    text: string;
    formatting: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      color?: string;
    }[];
  };
  tags: string[];
  isPinned: boolean;
  color: string;
  folderId?: string;
  role: Exclude<Role, 'all'>;
  createdAt: Date;
  updatedAt: Date;
}

export interface Folder {
  id: string;
  name: string;
  color?: string;
}

export function useNotes() {
  const { user } = useAuthContext();
  const [notes, setNotes] = useState<Note[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user) {
      fetchNotes();
      fetchFolders();
    }
  }, [user]);

  const fetchFolders = async () => {
    try {
      const { data, error } = await supabase
        .from('note_folders')
        .select('*')
        .eq('user_id', user?.id)
        .order('name');

      if (error) throw error;
      setFolders(data);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setError(err as Error);
    }
  };

  const addFolder = async (name: string, color?: string) => {
    try {
      const { error } = await supabase
        .from('note_folders')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          name,
          color
        });

      if (error) throw error;
      await fetchFolders();
    } catch (err) {
      console.error('Error adding folder:', err);
      throw err;
    }
  };

  const updateFolder = async (id: string, name: string, color?: string) => {
    try {
      const { error } = await supabase
        .from('note_folders')
        .update({ name, color })
        .eq('id', id);

      if (error) throw error;
      await fetchFolders();
    } catch (err) {
      console.error('Error updating folder:', err);
      throw err;
    }
  };

  const deleteFolder = async (id: string) => {
    try {
      const { error } = await supabase
        .from('note_folders')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchFolders();
    } catch (err) {
      console.error('Error deleting folder:', err);
      throw err;
    }
  };

 const fetchNotes = async () => {
  if (!user) return;
  
  try {
    setLoading(true);

    // Get owned notes
    const { data: ownedNotes, error: ownedError } = await supabase
      .from('notes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(5);

    if (ownedError) throw ownedError;

    // Get notes where user is a collaborator separately
    const { data: collaborations, error: collabError } = await supabase 
      .from('note_collaborators')
      .select('note_id')
      .eq('user_id', user.id);

    if (collabError) throw collabError;

    let collaborativeNotes = [];
    if (collaborations && collaborations.length > 0) {
      const noteIds = collaborations.map(c => c.note_id);
      const { data: collabNotes, error: fetchError } = await supabase
        .from('notes')
        .select('*')
        .in('id', noteIds)
        .order('created_at', { ascending: false })
        .limit(5);

      if (fetchError) throw fetchError;
      collaborativeNotes = collabNotes || [];
    }

    setNotes([...(ownedNotes || []), ...collaborativeNotes]);
  } catch (err) {
    console.error('Error fetching notes:', err);
    setError(err instanceof Error ? err : new Error('Failed to fetch notes'));
  } finally {
    setLoading(false);
  }
};

  const addNote = async (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Insert note
      const { data: noteData, error: noteError } = await supabase
        .from('notes')
        .insert({
          user_id: user.id,
          content: note.content,
          is_pinned: note.isPinned,
          color: note.color,
          folder_id: note.folderId,
          role: note.role
        })
        .select()
        .single();

      if (noteError) throw noteError;

      // Add tags
      if (note.tags.length > 0) {
        const { error: tagsError } = await supabase
          .from('note_tags')
          .insert(
            note.tags.map(tag => ({
              note_id: noteData.id,
              tag,
            }))
          );

        if (tagsError) throw tagsError;
      }

      // Add collaborators
      if (note.collaborators?.length > 0) {
        const { error: collabError } = await supabase
          .from('note_collaborators')
          .insert(
            note.collaborators.map(collab => ({
              note_id: noteData.id,
              user_id: collab.email, // In a real app, you'd need to look up the user ID from email
              role: collab.role
            }))
          );

        if (collabError) throw collabError;
      }

      await fetchNotes();
    } catch (err) {
      console.error('Error adding note:', err);
      throw err;
    }
  };

  const updateNote = async (id: string, updates: Partial<Note>) => {
    try {
      const { error } = await supabase
        .from('notes')
        .update({
          content: updates.content,
          is_pinned: updates.isPinned,
          color: updates.color,
          folder_id: updates.folderId,
          role: updates.role
        })
        .eq('id', id);

      if (error) throw error;

      // Update collaborators if provided
      if (updates.collaborators) {
        // Delete existing collaborators
        await supabase
          .from('note_collaborators')
          .delete()
          .eq('note_id', id);

        // Add new collaborators
        if (updates.collaborators.length > 0) {
          await supabase
            .from('note_collaborators')
            .insert(
              updates.collaborators.map(collab => ({
                note_id: id,
                user_id: collab.email, // In a real app, you'd need to look up the user ID from email
                role: collab.role
              }))
            );
        }
      }

      // Update tags
      if (updates.tags) {
        // Delete existing tags
        await supabase
          .from('note_tags')
          .delete()
          .eq('note_id', id);

        // Add new tags
        if (updates.tags.length > 0) {
          await supabase
            .from('note_tags')
            .insert(
              updates.tags.map(tag => ({
                note_id: id,
                tag,
              }))
            );
        }
      }

      await fetchNotes();
    } catch (err) {
      console.error('Error updating note:', err);
      throw err;
    }
  };

  const deleteNote = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchNotes();
    } catch (err) {
      console.error('Error deleting note:', err);
      throw err;
    }
  };

  return {
    notes,
    folders,
    loading,
    error,
    addNote,
    updateNote,
    deleteNote,
    addFolder,
    updateFolder,
    deleteFolder
  };
}